// navigation.js — FONTE UNICA della navigazione dell'app artigiano.
//
// Sidebar a 7 voci fisse: due VOCI DIRETTE (Home, Controllo Gestionale) che
// linkano a una pagina, seguite da cinque HUB (Operatività, Clienti & Vendite,
// Acquisti, mAIstro AI, Servizi) che aprono una pagina-hub a card. Le card e le
// voci dirette puntano alle ROUTE ESISTENTI: nessuna logica nuova.
//
// (La vecchia macro "Panoramica" è stata rimossa: Home e Controllo Gestionale
// sono ora voci dirette. "Assistente mAIstro" e "Proposte" non sono più in cima
// alla sidebar: restano come card dentro la Hub "mAIstro AI".)

import {
  Home, BarChart3, Hammer, HardHat, Calendar, Folder,
  Users, ClipboardList, Tag, Receipt, Truck, Euro,
  Sparkles, Activity, Newspaper, Lightbulb,
  Settings, Plug, Gift, BookOpen, TrendingUp, Plus, LifeBuoy,
} from 'lucide-react';

// Voci DIRETTE della sidebar (link a pagina, senza hub).
export const DIRECT = [
  { key: 'home', label: 'Home', to: '/', icon: Home, color: 'var(--zone-blue)' },
  { key: 'governance', label: 'Controllo Gestionale', to: '/governance', icon: BarChart3, color: 'var(--zone-blue)' },
];

// Le 5 HUB (macro-voci): `path` = route della Hub; `cards[].to` = route esistente.
export const MACROS = [
  {
    key: 'operativita', label: 'Operatività', path: '/operativita',
    icon: Hammer, color: 'var(--zone-green)',
    desc: 'Il lavoro sul campo: cantieri, agenda e documenti.',
    primary: { label: 'Nuovo cantiere', to: '/cantieri/nuovo', icon: Plus },
    cards: [
      { label: 'Cantieri', to: '/cantieri', icon: HardHat, description: 'Gestisci e monitora tutte le commesse.', cta: 'Apri cantieri' },
      { label: 'Calendario', to: '/calendario', icon: Calendar, description: 'Appuntamenti, sopralluoghi e scadenze.', cta: 'Apri calendario' },
      { label: 'Documenti', to: '/documenti', icon: Folder, description: 'Archivio contratti, fatture e foto per cliente e cantiere.', cta: 'Apri archivio' },
    ],
  },
  {
    key: 'clienti-vendite', label: 'Clienti & Vendite', path: '/clienti-vendite',
    icon: TrendingUp, color: 'var(--zone-amber)',
    desc: 'Chi sono i tuoi clienti e cosa gli proponi.',
    primary: { label: 'Nuovo preventivo', to: '/preventivi/nuovo', icon: Plus },
    cards: [
      { label: 'Clienti', to: '/clienti', icon: Users, description: 'Anagrafica, contatti e stato della relazione.', cta: 'Apri clienti' },
      { label: 'Preventivi', to: '/preventivi', icon: ClipboardList, description: 'Offerte e pipeline commerciale.', cta: 'Apri preventivi' },
      { label: 'Listino prezzi', to: '/listino', icon: Tag, description: 'Le voci riusabili nei tuoi preventivi.', cta: 'Apri listino' },
      { label: 'Fatture clienti', to: '/fatture', icon: Receipt, description: 'Fatturazione, incassi e invio allo SDI.', cta: 'Apri fatture' },
    ],
  },
  {
    key: 'acquisti', label: 'Acquisti', path: '/acquisti',
    icon: Truck, color: 'var(--zone-acqua)',
    desc: 'Fornitori e costi in ingresso.',
    cards: [
      { label: 'Fornitori e acquisti', to: '/fornitori', icon: Truck, description: 'Anagrafica fornitori e registrazione acquisti.', cta: 'Apri fornitori' },
      { label: 'Registro spese', dev: true, icon: Euro, description: 'Traccia i costi non legati a un cantiere.', cta: 'In arrivo' },
    ],
  },
  {
    key: 'maistro-ai', label: 'mAIstro AI', path: '/maistro-ai',
    icon: Sparkles, color: 'var(--zone-magenta)',
    desc: "L'assistente vocale e le automazioni intelligenti.",
    cards: [
      { label: 'Assistente mAIstro', to: '/assistente', icon: Sparkles, description: 'Mandagli un vocale: lui gestisce la carta.', cta: 'Apri assistente' },
      { label: 'Attività mAIstro', to: '/ai-monitor', icon: Activity, description: 'Monitor della pipeline vocale in tempo reale.', cta: 'Apri monitor' },
      { label: 'Novità Settore', to: '/novita', icon: Newspaper, description: 'Aggiornamenti utili dal tuo mestiere.', cta: 'Leggi' },
      { label: 'Proposte', dev: true, icon: Lightbulb, description: 'Le prossime automazioni che mAIstro può fare per te.', cta: 'In sviluppo' },
    ],
  },
  {
    key: 'servizi', label: 'Servizi', path: '/servizi',
    icon: Settings, color: 'var(--zone-slate)',
    desc: "Collegamenti, aiuto e impostazioni dell'account.",
    cards: [
      { label: 'Integrazioni', to: '/integrazioni', icon: Plug, description: 'Collega WhatsApp, Fatture in Cloud ed email.', cta: 'Apri collegamenti' },
      { label: 'Invita e guadagna', to: '/invita', icon: Gift, description: 'Porta un collega e ricevi un premio.', cta: 'Invita' },
      { label: 'Guida', to: '/guida', icon: BookOpen, description: 'Come sfruttare mAIstro al meglio.', cta: 'Apri guida' },
      { label: 'Supporto', to: '/supporto', icon: LifeBuoy, description: 'Apri un ticket o chatta con noi.', cta: 'Contattaci' },
      { label: 'Impostazioni', to: '/impostazioni', icon: Settings, description: 'Profilo azienda, team e piano.', cta: 'Apri impostazioni' },
    ],
  },
];

// Ordine della sidebar: voci dirette + hub.
export const SIDEBAR = [...DIRECT, ...MACROS];

export function getMacro(key) {
  return MACROS.find((m) => m.key === key) || null;
}

const matchesRoute = (routeTo, pathname) =>
  routeTo && (routeTo === pathname || (routeTo !== '/' && pathname.startsWith(routeTo + '/')));

// Data una route, individua la macro/voce e la card di appartenenza.
export function resolveNav(pathname) {
  if (pathname === '/') return { direct: DIRECT[0], isHub: false };
  const direct = DIRECT.find((d) => d.to !== '/' && matchesRoute(d.to, pathname));
  if (direct) return { direct, isHub: false };
  const hub = MACROS.find((m) => m.path === pathname);
  if (hub) return { macro: hub, card: null, isHub: true };
  for (const m of MACROS) {
    for (const c of m.cards) {
      if (matchesRoute(c.to, pathname)) return { macro: m, card: c, isHub: false };
    }
  }
  return null;
}

// Key della voce di sidebar attiva per una route (per l'evidenziazione).
export function activeSidebarKey(pathname) {
  if (pathname === '/') return 'home';
  const direct = DIRECT.find((d) => d.to !== '/' && matchesRoute(d.to, pathname));
  if (direct) return direct.key;
  for (const m of MACROS) {
    if (pathname === m.path) return m.key;
    for (const c of m.cards) {
      if (matchesRoute(c.to, pathname)) return m.key;
    }
  }
  return null;
}

// Breadcrumb "Home / Macro / Sotto" (l'ultimo elemento è la posizione corrente).
export function buildBreadcrumb(pathname) {
  if (pathname === '/') return [{ label: 'Home' }];
  const direct = DIRECT.find((d) => d.to !== '/' && matchesRoute(d.to, pathname));
  if (direct) return [{ label: 'Home', to: '/' }, { label: direct.label }];
  const hub = MACROS.find((m) => m.path === pathname);
  if (hub) return [{ label: 'Home', to: '/' }, { label: hub.label }];
  for (const m of MACROS) {
    const c = m.cards.find((x) => matchesRoute(x.to, pathname));
    if (c) return [{ label: 'Home', to: '/' }, { label: m.label, to: m.path }, { label: c.label }];
  }
  return null;
}

// Trova la voce/card per una route: usata dalla barra Preferiti (label + icona).
export function findCardByRoute(to) {
  const d = DIRECT.find((x) => x.to === to);
  if (d) return { ...d, macro: { color: d.color } };
  for (const m of MACROS) {
    const c = m.cards.find((x) => x.to === to);
    if (c) return { ...c, macro: m };
  }
  return null;
}
